import { useState, useEffect, useRef, useCallback } from 'react';

import {
  StringGroupItem,
  useListStringGroupsQuery,
} from 'src/generated/graphql';
import {
  STRING_GROUPS_FETCH_BATCH_SIZE,
  STRING_GROUPS_INITIAL_BATCHES,
} from 'src/features/namespace/constants';

export interface UseStringGroupsDataResult {
  /** All string groups loaded so far for the current namespace. */
  displayItems: StringGroupItem[];
  /** Whether a Load More request is in flight. */
  isLoadingMore: boolean;
  /** Whether more items can be loaded from the server. */
  hasMore: boolean;
  /** Fetches the next batch of items. */
  handleLoadMore: () => void;
  /** True during the initial page load. */
  initialLoading: boolean;
  /** Error from the most recent query, if any. */
  error: Error | undefined;
}

/**
 * Fetches and manages string groups data for a namespace.
 *
 * Auto-chains STRING_GROUPS_INITIAL_BATCHES×STRING_GROUPS_FETCH_BATCH_SIZE on
 * mount for progressive loading, then exposes Load More for the remainder.
 *
 * Filtering is NOT handled here — StringGroupsTable filters the loaded items
 * client-side via useCollection, so there is no server-side prefix search.
 *
 * Uses `useListStringGroupsQuery` (reactive hook) which works correctly with
 * both MockedProvider (mock mode) and ApolloProvider (real mode).
 */
export function useStringGroupsData(
  namespaceId: string | undefined,
): UseStringGroupsDataResult {
  // --- Loaded data state ---
  const [allItems, setAllItems] = useState<StringGroupItem[]>([]);
  const [browseToken, setBrowseToken] = useState<string | null>(null);
  const [browseInitialized, setBrowseInitialized] = useState(false);

  // --- Auto-chain state ---
  /** How many batches have been fetched so far (1 = initial query result). */
  const [batchesFetched, setBatchesFetched] = useState(0);
  /** Whether the auto-chain is still running. */
  const [isAutoChaining, setIsAutoChaining] = useState(false);
  /** Ref to track abort conditions during async chain iterations. */
  const autoChainAbortRef = useRef(false);
  /** Ref to track the namespaceId the chain was started for. */
  const chainNamespaceRef = useRef<string | undefined>(undefined);
  /** Ref guard to prevent concurrent Load More calls from double-clicks. */
  const isLoadingMoreRef = useRef(false);

  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const {
    data: browseData,
    loading: browseLoading,
    error: browseError,
    fetchMore: browseFetchMore,
  } = useListStringGroupsQuery({
    variables: {
      namespaceId: namespaceId ?? '',
      includeLatestSnapshot: true,
      pageSize: STRING_GROUPS_FETCH_BATCH_SIZE,
    },
    skip: !namespaceId,
    // Bypass the Apollo cache entirely — string group data is accumulated in
    // local state (allItems) and must always be fresh. fetchMore below supplies
    // the `updateQuery` that 'no-cache' requires.
    fetchPolicy: 'no-cache',
  });

  // --- Process the initial response: seed items and kick off the auto-chain ---
  useEffect(() => {
    if (!browseData?.listStringGroups) return;

    const items = browseData.listStringGroups.stringGroups;
    const nextToken = browseData.listStringGroups.nextToken ?? null;

    if (!browseInitialized) {
      // First load — set items directly and start auto-chain
      setAllItems(items);
      setBrowseToken(nextToken);
      setBrowseInitialized(true);
      setBatchesFetched(1);

      // Start auto-chain if there are more pages to fetch
      if (nextToken && STRING_GROUPS_INITIAL_BATCHES > 1) {
        setIsAutoChaining(true);
        autoChainAbortRef.current = false;
        chainNamespaceRef.current = namespaceId;
      }
    }
  }, [browseData, browseInitialized, namespaceId]);

  // --- Auto-chain: fetch remaining batches after initial load ---
  useEffect(() => {
    if (!isAutoChaining || !browseInitialized) return;
    if (!browseToken || batchesFetched >= STRING_GROUPS_INITIAL_BATCHES) {
      // Chain complete — either no more data or reached target batch count
      setIsAutoChaining(false);
      return;
    }

    // Abort conditions checked before starting the fetch
    /* istanbul ignore if -- defensive: abort+isAutoChaining never both true in same batch */
    if (autoChainAbortRef.current) {
      setIsAutoChaining(false);
      return;
    }

    let cancelled = false;

    const fetchNextBatch = async () => {
      try {
        const result = await browseFetchMore({
          variables: {
            nextToken: browseToken,
          },
          // Required by Apollo when fetchPolicy is 'no-cache' (error 29).
          // This hook accumulates pages in local state, so the merge here is
          // an identity — we only consume fetchMore's return value.
          updateQuery: (_prev, { fetchMoreResult }) => fetchMoreResult,
        });

        // Check abort conditions after async operation
        if (cancelled || autoChainAbortRef.current) {
          return;
        }

        // Verify we're still on the same namespace
        /* istanbul ignore if -- defensive: cleanup cancels async before namespace drift */
        if (chainNamespaceRef.current !== namespaceId) {
          setIsAutoChaining(false);
          return;
        }

        const newItems = result.data.listStringGroups.stringGroups;
        const newToken = result.data.listStringGroups.nextToken ?? null;

        setAllItems((prev) => [...prev, ...newItems]);
        setBrowseToken(newToken);
        setBatchesFetched((prev) => prev + 1);

        // If no more data, stop the chain
        if (!newToken) {
          setIsAutoChaining(false);
        }
      } catch (error) {
        // On error, stop the chain gracefully
        /* istanbul ignore next -- untestable: Apollo's fetchMore resolves with { error } rather than rejecting; a genuine rejection requires a network-level failure that WildcardMockLink cannot simulate */
        console.error('[useStringGroupsData] Auto-chain fetch failed:', error);
        /* istanbul ignore next */
        setIsAutoChaining(false);
      }
    };

    fetchNextBatch();

    return () => {
      cancelled = true;
    };
  }, [
    isAutoChaining,
    browseInitialized,
    browseToken,
    batchesFetched,
    browseFetchMore,
    namespaceId,
  ]);

  // Reset all state when namespace changes
  useEffect(() => {
    autoChainAbortRef.current = true;
    setIsAutoChaining(false);
    setAllItems([]);
    setBrowseToken(null);
    setBrowseInitialized(false);
    setBatchesFetched(0);
  }, [namespaceId]);

  // --- Load More handler (user-triggered, only active after auto-chain) ---
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMoreRef.current || isAutoChaining) return;
    isLoadingMoreRef.current = true;

    setIsLoadingMore(true);

    try {
      if (!browseToken || !browseFetchMore) return;

      const result = await browseFetchMore({
        variables: {
          nextToken: browseToken,
        },
        // Required by Apollo when fetchPolicy is 'no-cache' (error 29).
        // This hook accumulates pages in local state, so the merge here is
        // an identity — we only consume fetchMore's return value.
        updateQuery: (_prev, { fetchMoreResult }) => fetchMoreResult,
      });

      const newItems = result.data.listStringGroups.stringGroups;
      const newToken = result.data.listStringGroups.nextToken ?? null;

      setAllItems((prev) => [...prev, ...newItems]);
      setBrowseToken(newToken);
    } catch (error) {
      /* istanbul ignore next -- untestable: same as auto-chain; Apollo fetchMore does not reject in mock environments */
      console.error('[useStringGroupsData] Load more failed:', error);
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [isAutoChaining, browseToken, browseFetchMore]);

  // hasMore: only report true AFTER the auto-chain is complete and there is
  // still a token. During auto-chain, report false so the Load More button
  // stays disabled (progressive data appears via the header count).
  const hasMore = !isAutoChaining && !!browseToken;

  return {
    displayItems: allItems,
    isLoadingMore,
    hasMore,
    handleLoadMore,
    initialLoading: browseLoading && allItems.length === 0,
    error: browseError,
  };
}
